import React, { ReactNode } from 'react';

import { Anchor, Button, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { IconCalendar, IconClock, IconMapPin, IconUser } from '@tabler/icons-react';
import { ReactComponent as AppStoreBadge } from 'assets/svg/app_store_badge.svg';
import { ReactComponent as GooglePlayBadge } from 'assets/svg/google_play_badge.svg';
import KiloAlert from 'components/KiloAlert';
import dayjs from 'dayjs';
import { PublicKiloClassAgenda } from 'types';

import { KILO_ATHLETE_APP_LINKS, resolveOpenAppHref } from './appLinks';
import {
  ClassConfirmationVariant,
  ConfirmationVariant,
  isClassConfirmationVariant,
} from './confirmationVariant';

export interface PurchaseConfirmationProps {
  variant: ConfirmationVariant;
  /** Shown as a status line on every variant; also drops the "waiver signed" copy. */
  waiverPending?: boolean;
  /** A $0 purchase settles with no payment, so no receipt is sent. */
  noPaymentTaken?: boolean;
  /** The pre-selected class, for the booked / filled variants. */
  kiloClass?: PublicKiloClassAgenda | null;
  /** "Open My Schedule": the caller switches profile and navigates to the athlete web app. */
  onOpenSchedule: () => void;
  isOpeningSchedule?: boolean;
  /** Optional purchase summary slot (the package summary card). */
  children?: ReactNode;
}

const WAIVER_PENDING_COPY =
  'Pending signature: we emailed the liability waiver to the athlete — their access activates once they sign it from their own account.';

interface ClassOutcomeCopy {
  title: string;
  body: (className: string, classDate: string) => string;
  scheduleLabel: string;
  appHint: string;
}

const OPEN_SCHEDULE = 'Open My Schedule';
const BOOK_ANOTHER = 'Book another class from My Schedule';
const MANAGE_IN_APP = 'Download the app to manage your bookings on the go:';
const PICK_IN_APP = 'Or download the app and pick a class from there:';
const FALLBACK_CLASS_NAME = 'this class';
const FALLBACK_CLASS_DATE = 'this day';

/** Copy per class outcome; the body is given the pre-selected class's name and date. */
const CLASS_OUTCOME_COPY: Record<ClassConfirmationVariant, ClassOutcomeCopy> = {
  classBooked: {
    title: "You're booked!",
    body: className => `Your package was purchased successfully, and your spot in ${className} is reserved.`,
    scheduleLabel: OPEN_SCHEDULE,
    appHint: MANAGE_IN_APP,
  },
  classAlreadyBooked: {
    title: "You're already booked!",
    body: className =>
      `Your package was purchased successfully. You already have a reservation for ${className}, so no new reservation was needed.`,
    scheduleLabel: OPEN_SCHEDULE,
    appHint: MANAGE_IN_APP,
  },
  classCredited: {
    title: 'Your existing reservation was applied',
    body: className =>
      `Your package was purchased successfully, and your existing reservation for ${className} has been applied to this package.`,
    scheduleLabel: OPEN_SCHEDULE,
    appHint: MANAGE_IN_APP,
  },
  classPromotedFromWaitlist: {
    title: "You're off the waitlist and booked!",
    body: className =>
      `Your package was purchased successfully, and your spot in ${className} is now reserved and paid for with a credit from this package.`,
    scheduleLabel: OPEN_SCHEDULE,
    appHint: MANAGE_IN_APP,
  },
  classStillWaitlisted: {
    title: "You're still on the waitlist",
    body: className =>
      `Your package was purchased successfully. You're still on the waitlist for ${className} — now with credits, so if a spot opens we'll book you automatically with this package.`,
    scheduleLabel: OPEN_SCHEDULE,
    appHint: MANAGE_IN_APP,
  },
  classFilled: {
    title: 'The class is full',
    body: () =>
      'Your package was purchased successfully, but the class filled up before we could reserve your spot. You can use your package to book another class.',
    scheduleLabel: BOOK_ANOTHER,
    appHint: PICK_IN_APP,
  },
  classNotRebookable: {
    title: "This class can't be booked again",
    body: () =>
      'Your package was purchased successfully, but this class is no longer available for a new reservation. You can use your package to book another class.',
    scheduleLabel: BOOK_ANOTHER,
    appHint: PICK_IN_APP,
  },
  classDailyLimitReached: {
    title: "Your class wasn't reserved",
    body: (_className, classDate) =>
      `Your package is active, but this class wasn’t reserved. Your gym allows one class reservation per day, and you already have a reservation on ${classDate}. To take this class instead, cancel the other one from My Schedule, or book a class on another day.`,
    scheduleLabel: BOOK_ANOTHER,
    appHint: PICK_IN_APP,
  },
};

const StoreBadges = (): JSX.Element => (
  <Group spacing='sm' position='center' data-cy='storeBadges'>
    <Anchor
      href={KILO_ATHLETE_APP_LINKS.appStore}
      target='_blank'
      rel='noopener noreferrer'
      data-cy='appStoreBadge'
    >
      <AppStoreBadge height={44} />
    </Anchor>
    <Anchor
      href={KILO_ATHLETE_APP_LINKS.googlePlay}
      target='_blank'
      rel='noopener noreferrer'
      data-cy='googlePlayBadge'
    >
      <GooglePlayBadge height={44} />
    </Anchor>
  </Group>
);

const ClassDetails = ({ kiloClass }: { kiloClass: PublicKiloClassAgenda }): JSX.Element => {
  const startTime = dayjs(`2000-01-01 ${kiloClass.startTime}`).format('h:mm A');
  const endTime = kiloClass.endTime ? dayjs(`2000-01-01 ${kiloClass.endTime}`).format('h:mm A') : '';
  return (
    <Paper withBorder radius='md' p='md' data-cy='confirmationClassDetails'>
      <Stack spacing='xs'>
        <Text fw={600}>{kiloClass.className}</Text>
        <Group spacing='xs' noWrap>
          <IconCalendar size={18} />
          <Text size='sm'>{dayjs(kiloClass.startDate).format('dddd, MMMM D, YYYY')}</Text>
        </Group>
        <Group spacing='xs' noWrap>
          <IconClock size={18} />
          <Text size='sm'>{endTime ? `${startTime} to ${endTime}` : startTime}</Text>
        </Group>
        <Group spacing='xs' noWrap>
          <IconUser size={18} />
          <Text size='sm'>{kiloClass.coaches.join(', ') || 'No coach assigned'}</Text>
        </Group>
        {kiloClass.location && (
          <Group spacing='xs' noWrap>
            <IconMapPin size={18} />
            <Text size='sm'>{kiloClass.location}</Text>
          </Group>
        )}
      </Stack>
    </Paper>
  );
};

// The single post-purchase confirmation shared by the public sales portal and the
// athlete-side portal (spec Item 1). Presentational: the caller derives the variant from
// the checkout response and owns the "Open My Schedule" navigation.
const PurchaseConfirmation = ({
  variant,
  waiverPending = false,
  noPaymentTaken = false,
  kiloClass,
  onOpenSchedule,
  isOpeningSchedule = false,
  children,
}: PurchaseConfirmationProps): JSX.Element => {
  const receiptLine = [
    noPaymentTaken ? null : 'Receipt sent to your email.',
    waiverPending ? null : 'Your waiver is signed and on file.',
  ]
    .filter(Boolean)
    .join(' ');

  const scheduleButton = (label: string, primary: boolean): JSX.Element => (
    <Button
      variant={primary ? 'filled' : 'subtle'}
      size={primary ? 'md' : 'sm'}
      onClick={onOpenSchedule}
      loading={isOpeningSchedule}
      disabled={isOpeningSchedule}
      data-cy='openScheduleButton'
    >
      {label}
    </Button>
  );

  const classCopy = isClassConfirmationVariant(variant) ? CLASS_OUTCOME_COPY[variant] : null;
  const title = classCopy ? classCopy.title : "You're in!";

  return (
    <Stack spacing='lg' align='center' px='md' data-cy='purchaseConfirmation' data-variant={variant}>
      <Title order={2} align='center' fw={700} data-cy='purchaseConfirmationTitle'>
        {title}
      </Title>

      {classCopy ? (
        <>
          <Text align='center' maw={520} data-cy='purchaseConfirmationBody'>
            {classCopy.body(
              kiloClass?.className ?? FALLBACK_CLASS_NAME,
              kiloClass ? dayjs(kiloClass.startDate).format('dddd, MMMM D') : FALLBACK_CLASS_DATE,
            )}
          </Text>
          <Text size='sm' align='center' color='dimmed' maw={520}>
            {receiptLine}
          </Text>
        </>
      ) : (
        <Text align='center' color='dimmed' maw={520} data-cy='purchaseConfirmationBody'>
          {[receiptLine, variant === 'newUser' && "Next: get the app — that's where you book your classes."]
            .filter(Boolean)
            .join(' ')}
        </Text>
      )}

      {waiverPending && (
        <KiloAlert variantStyle='warning' data-cy='waiverPendingNotice' maw={560}>
          {WAIVER_PENDING_COPY}
        </KiloAlert>
      )}

      {classCopy && kiloClass && <ClassDetails kiloClass={kiloClass} />}

      {variant === 'newUser' && (
        <Stack spacing='sm' align='center' maw={560}>
          <Title order={4} align='center'>
            One last step — Download the app to book your first session.
          </Title>
          <Text size='sm' align='center' color='dimmed'>
            Sessions are booked through the app. Get it now, sign in with the account you just created, and
            reserve your spot — sessions fill up fast.
          </Text>
          <StoreBadges />
          <Text size='sm' align='center'>
            Already have the app?{' '}
            <Anchor href={resolveOpenAppHref()} data-cy='openAppLink'>
              Open it →
            </Anchor>
          </Text>
          {scheduleButton('Open My Schedule', false)}
        </Stack>
      )}

      {variant === 'hasApp' && (
        <Stack spacing='sm' align='center' maw={560}>
          <Title order={4} align='center'>
            Open the app to book your first session.
          </Title>
          <Text size='sm' align='center' color='dimmed'>
            Your new package is already active on your account. Jump back into the app to browse the schedule
            and reserve your spot.
          </Text>
          <Button component='a' href={resolveOpenAppHref()} size='md' data-cy='openAppButton'>
            Open the Kilo app
          </Button>
          <Text size='xs' color='dimmed' align='center'>
            New device? Get the app again:
          </Text>
          <StoreBadges />
          {scheduleButton('Open My Schedule', false)}
        </Stack>
      )}

      {variant === 'linkedAthletePending' && (
        <Stack spacing='sm' align='center' maw={560}>
          <Text size='sm' align='center' color='dimmed'>
            Sessions are booked through the app — share the download links with the athlete so they can
            reserve their spot once their waiver is signed.
          </Text>
          <StoreBadges />
          {scheduleButton('Open My Schedule', false)}
        </Stack>
      )}

      {classCopy && (
        <Stack spacing='sm' align='center' maw={560}>
          {scheduleButton(classCopy.scheduleLabel, true)}
          <Text size='xs' color='dimmed' align='center'>
            {classCopy.appHint}
          </Text>
          <StoreBadges />
        </Stack>
      )}

      {children}
    </Stack>
  );
};

export default PurchaseConfirmation;
